import { useCallback, useEffect, useMemo, useState } from 'react'
import { MessageSquare, Loader2, ChevronRight } from 'lucide-react'
import { OpenDestinationIcons } from '@/lib/open-destination-icons'
import { Button } from '@/components/ui/button'
import {
  formatThreadDate,
  listAgentChatThreads,
  type AgentChatThread,
} from '@/lib/api/threads'
import { openAgentChatThread } from '@/lib/open-agent-chat-thread'
import { openSidebarMode } from '@/lib/sidebar-mode'
import { useChatSurfaceRegistryOptional } from '@/hooks/use-chat-surface-registry'
import { useWorkspace } from '@/providers/workspace-provider'
import { isThreadWorkspaceVisible } from '@/components/features/inbox/mappers'
import { workspaceListRowChrome } from '@/components/features/inbox/workspace-accent'
import {
  HomeFeedList,
  HomeFeedRow,
  HomeWidgetShell,
} from '@/components/dashboard/home-widget-shell'
import { HomeFeedContextMenu } from '@/components/dashboard/home-feed-context-menu'
import {
  homeSelectionKey,
  type HomeContinuityProps,
  type HomeSelection,
} from '@/components/dashboard/home-selection'
import { testIds } from '@/lib/test-ids'

const FEED_LIMIT = 5
/** Fetch enough rows that client-side workspace scope can still fill the feed. */
const FETCH_LIMIT = 40

function toSelection(thread: AgentChatThread): HomeSelection {
  return { kind: 'chat', threadId: thread.id, title: thread.title }
}

interface HomeRecentChatsFeedSectionProps {
  onChatThreadClick?: (
    threadId: string,
    title: string,
    event?: React.MouseEvent,
  ) => void
  continuity?: HomeContinuityProps
}

export function HomeRecentChatsFeedSection({
  onChatThreadClick,
  continuity,
}: HomeRecentChatsFeedSectionProps) {
  const { activeWorkspaceIds, workspaces } = useWorkspace()
  const registry = useChatSurfaceRegistryOptional()
  const [threads, setThreads] = useState<AgentChatThread[]>([])
  const [loading, setLoading] = useState(true)
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    thread: AgentChatThread
  } | null>(null)

  const loadThreads = useCallback(async () => {
    try {
      const rows = await listAgentChatThreads(undefined, FETCH_LIMIT)
      setThreads(rows)
    } catch {
      setThreads([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadThreads()
    const onFocus = () => void loadThreads()
    window.addEventListener('focus', onFocus)
    const onRefresh = () => void loadThreads()
    window.addEventListener('chats-sidebar-refresh', onRefresh)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('chats-sidebar-refresh', onRefresh)
    }
  }, [loadThreads])

  useEffect(() => {
    if (!contextMenu) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    const handleClick = () => setContextMenu(null)
    document.addEventListener('keydown', handleKey)
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [contextMenu])

  const feedThreads = useMemo(() => {
    const scoped = threads.filter((t) =>
      isThreadWorkspaceVisible(
        t.workspaceId ?? 'personal',
        activeWorkspaceIds,
        t.taggedOrganizationIds,
      ),
    )
    return scoped.slice(0, FEED_LIMIT)
  }, [activeWorkspaceIds, threads])

  const openChatsSidebar = () => openSidebarMode('chats')

  const selectThread = (thread: AgentChatThread, opts?: { openPreview?: boolean }) => {
    continuity?.selectHome(toSelection(thread), opts)
  }

  const openThread = (thread: AgentChatThread, event?: React.MouseEvent) => {
    if (continuity) {
      continuity.openSelection(toSelection(thread), event)
      return
    }
    if (onChatThreadClick) {
      onChatThreadClick(thread.id, thread.title, event)
      return
    }
    openChatsSidebar()
  }

  /** Match Chats sidebar: explicit surface choice (editor tab vs agent panel). */
  const openThreadSurface = (
    thread: AgentChatThread,
    options: { openInNewTab?: boolean; forceEditor?: boolean; forceAgentPanel?: boolean },
  ) => {
    openAgentChatThread(registry, thread.id, {
      title: thread.title,
      openInNewTab: options.openInNewTab,
      forceEditor: options.forceEditor,
      forceAgentPanel: options.forceAgentPanel,
    })
  }

  return (
    <HomeWidgetShell
      title="Recent Chats"
      icon={<MessageSquare />}
      data-testid={testIds.home.recentChatsFeed}
      className="min-h-[180px]"
      footer={
        <Button variant="ghost" size="sm" className="w-full h-8" onClick={openChatsSidebar}>
          Open Chats in sidebar
          <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      }
    >
      {contextMenu && (
        <HomeFeedContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onOpen={() => openThread(contextMenu.thread)}
          onPreview={() => selectThread(contextMenu.thread, { openPreview: true })}
          workspaces={continuity?.workspaces ?? workspaces}
          primaryActions={[
            {
              icon: <OpenDestinationIcons.openInNewTab className="h-3.5 w-3.5" />,
              label: 'Open in new tab',
              testId: testIds.home.contextMenuOpenInNewTab,
              action: () => {
                openThreadSurface(contextMenu.thread, {
                  openInNewTab: true,
                  forceEditor: true,
                })
                setContextMenu(null)
              },
            },
            {
              icon: <OpenDestinationIcons.openInAgentPanel className="h-3.5 w-3.5" />,
              label: 'Open in agent panel',
              testId: testIds.home.contextMenuOpenInAgentPanel,
              action: () => {
                openThreadSurface(contextMenu.thread, { forceAgentPanel: true })
                setContextMenu(null)
              },
            },
          ]}
        />
      )}

      {loading && (
        <div className="flex flex-1 items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && feedThreads.length === 0 && (
        <p className="text-sm text-muted-foreground py-6 text-center flex-1">No chats yet</p>
      )}

      {!loading && feedThreads.length > 0 && (
        <HomeFeedList>
          {feedThreads.map((thread) => {
            const tagIds = thread.taggedOrganizationIds ?? []
            const primaryWsId =
              tagIds.find((id) => workspaces.some((w) => w.id === id))
              ?? thread.workspaceId
              ?? undefined
            const workspaceColor = primaryWsId
              ? workspaces.find((w) => w.id === primaryWsId)?.color ?? undefined
              : undefined
            const isSelected =
              continuity != null &&
              homeSelectionKey(toSelection(thread)) === continuity.selectedKey
            const rowChrome = workspaceListRowChrome(workspaceColor, isSelected, {
              showRail: continuity?.showWorkspaceRails ?? activeWorkspaceIds.length > 1,
            })
            return (
              <HomeFeedRow
                key={thread.id}
                title={thread.title}
                meta="Chat"
                trailing={formatThreadDate(thread.lastMessageAt ?? thread.updatedAt)}
                selected={isSelected}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey) {
                    selectThread(thread)
                    openThread(thread, event)
                    return
                  }
                  selectThread(thread)
                }}
                onDoubleClick={(event) => {
                  selectThread(thread)
                  openThread(thread, event)
                }}
                onContextMenu={(e) => {
                  e.preventDefault()
                  selectThread(thread)
                  setContextMenu({ x: e.clientX, y: e.clientY, thread })
                }}
                className={rowChrome.className}
                style={rowChrome.style}
              />
            )
          })}
        </HomeFeedList>
      )}
    </HomeWidgetShell>
  )
}
